import React, { useState, useCallback, useRef, useEffect } from 'react';
import { GameProps, GameDef, playAreaStyle } from './types';

// ── BUBBLE POP CHAIN ─────────────────────────────────────────────────────────
// Grid of colored bubbles; tap a group of 2+ same-color touching bubbles to pop
// them all. Bigger groups score more (n²). No timer — ends when no group of 2+
// remains (or the board clears).

const COLS = 6;
const ROWS = 7;
const COLORS = ['🔴', '🟡', '🔵', '🟢', '🟣'];

type Grid = (string | null)[][]; // [row][col]

function buildGrid(): Grid {
  return Array.from({ length: ROWS }, () =>
    Array.from({ length: COLS }, () => COLORS[Math.floor(Math.random() * COLORS.length)])
  );
}

// Flood-fill same-color neighbors from (r,c).
function findGroup(grid: Grid, r: number, c: number): [number, number][] {
  const color = grid[r][c];
  if (!color) return [];
  const seen = new Set<string>();
  const stack: [number, number][] = [[r, c]];
  const group: [number, number][] = [];
  while (stack.length) {
    const [cr, cc] = stack.pop()!;
    const key = `${cr},${cc}`;
    if (seen.has(key)) continue;
    if (cr < 0 || cr >= ROWS || cc < 0 || cc >= COLS) continue;
    if (grid[cr][cc] !== color) continue;
    seen.add(key);
    group.push([cr, cc]);
    stack.push([cr - 1, cc], [cr + 1, cc], [cr, cc - 1], [cr, cc + 1]);
  }
  return group;
}

// Gravity: bubbles fall into gaps, then empty columns shift left.
function collapse(grid: Grid): Grid {
  const cols: (string | null)[][] = [];
  for (let c = 0; c < COLS; c++) {
    const col: string[] = [];
    for (let r = ROWS - 1; r >= 0; r--) {
      if (grid[r][c]) col.push(grid[r][c]!);
    }
    while (col.length < ROWS) col.push(null as unknown as string);
    cols.push(col);
  }
  // drop empty columns (all null) to the right
  const nonEmpty = cols.filter(col => col.some(Boolean));
  while (nonEmpty.length < COLS) nonEmpty.push(Array(ROWS).fill(null));
  // rebuild [row][col] with row 0 at top
  const out: Grid = Array.from({ length: ROWS }, () => Array(COLS).fill(null));
  for (let c = 0; c < COLS; c++) {
    for (let r = 0; r < ROWS; r++) {
      out[ROWS - 1 - r][c] = nonEmpty[c][r] ?? null;
    }
  }
  return out;
}

function hasMoves(grid: Grid): boolean {
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      if (grid[r][c] && findGroup(grid, r, c).length >= 2) return true;
    }
  }
  return false;
}

function BubblePop({ theme, isKid, onEnd }: GameProps) {
  const [grid, setGrid] = useState<Grid>(buildGrid);
  const [score, setScore] = useState(0);
  const [gain, setGain] = useState<{ pts: number; id: number } | null>(null);
  const scoreRef = useRef(0);
  const doneRef = useRef(false);

  useEffect(() => {
    if (doneRef.current) return;
    const cleared = grid.every(row => row.every(cell => !cell));
    if (cleared || !hasMoves(grid)) {
      doneRef.current = true;
      const t = setTimeout(() => onEnd({ score: scoreRef.current, noun: 'points' }), 600);
      return () => clearTimeout(t);
    }
  }, [grid, onEnd]);

  const pop = useCallback((r: number, c: number) => {
    setGrid(prev => {
      const group = findGroup(prev, r, c);
      if (group.length < 2) return prev;
      const pts = group.length * group.length; // n² rewards bigger clusters
      scoreRef.current += pts;
      setScore(scoreRef.current);
      setGain({ pts, id: Date.now() });
      setTimeout(() => setGain(null), 600);
      const next = prev.map(row => [...row]);
      group.forEach(([gr, gc]) => { next[gr][gc] = null; });
      return collapse(next);
    });
  }, []);

  return (
    <>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontWeight: 800, fontSize: isKid ? '1.3rem' : '1.1rem',
      }}>
        <span>🌈 {score} pts</span>
        {gain && (
          <span style={{ color: '#22c55e', animation: 'reward-pop-in 0.2s ease-out' }}>
            +{gain.pts} 💥
          </span>
        )}
      </div>

      <div style={{
        ...playAreaStyle(theme),
        display: 'grid',
        gridTemplateColumns: `repeat(${COLS}, 1fr)`,
        gridTemplateRows: `repeat(${ROWS}, 1fr)`,
        gap: '3px',
        padding: '10px',
      }}>
        {grid.flatMap((row, r) =>
          row.map((cell, c) => (
            <button
              key={`${r}-${c}`}
              onClick={() => cell && pop(r, c)}
              aria-label={cell ? 'Bubble' : 'Empty'}
              disabled={!cell}
              style={{
                background: 'none',
                border: 'none',
                cursor: cell ? 'pointer' : 'default',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isKid ? '1.9rem' : '1.7rem',
                lineHeight: 1,
                padding: 0,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {cell ? <span style={{ animation: 'reward-pop-in 0.18s ease-out' }}>{cell}</span> : ''}
            </button>
          ))
        )}
      </div>
      <div style={{ textAlign: 'center', fontSize: '0.8rem', marginTop: '6px', opacity: 0.7 }}>
        tap groups of 2+ same color • bigger groups = more points
      </div>
    </>
  );
}

export const BUBBLE_POP: GameDef = {
  id: 'bubble-pop',
  title: 'Bubble Pop',
  emoji: '🫧',
  instructions: 'Tap groups of 2 or more touching bubbles of the same color. Bigger groups score more!',
  verb: 'Pop',
  Component: BubblePop,
};

export default BubblePop;
